/**
 * Unified template variable resolution for DynamicTableV2.
 *
 * All string templates in schema definitions — endpoints, payloads, router state,
 * confirm dialog text, file names, success/error messages — are resolved through
 * the same two functions: `resolveTemplate` (strings) and `resolveValue` (any JSON value).
 *
 * ## Available tokens in string templates
 *
 * | Token                | Resolves to                                              |
 * |----------------------|----------------------------------------------------------|
 * | `{rowId}`            | The resolved row ID (from `dataSource.rowIdField`)       |
 * | `{row.fieldName}`    | `row[fieldName]` coerced to string                       |
 * | `{count}`            | Selected row count (respects select-all-pages)           |
 * | `{selectedCount}`    | `selectedKeys.length` (current page selection only)      |
 * | `{totalCount}`       | Server-side total record count                           |
 *
 * ## Special sentinel values in payload / state objects
 *
 * These must be the *entire* value of the field (not embedded in a larger string)
 * and resolve to a typed (non-string) result:
 *
 * | Sentinel value        | Resolves to                                       |
 * |-----------------------|---------------------------------------------------|
 * | `"{selectedRowIds}"`  | `string[]` — currently selected row IDs           |
 * | `"{deselectedRowIds}"`| `string[]` — row IDs deselected during select-all |
 * | `"{selectAllActive}"` | `boolean`  — whether "select all pages" is on     |
 * | `"{tableQuery}"`      | `object`   — current Filter/Sort/Pagination state |
 * | `"...{tableQuery}"`   | Spread — merges tableQuery keys into parent object |
 */

export interface TemplateContext {
  /** The current row's data object (has `_resolvedRowId` pre-populated). */
  row?: Record<string, unknown>;
  /** IDs of currently checked rows on the current page. */
  selectedKeys?: string[];
  /** True when "Select All Pages" mode is active. */
  selectAllActive?: boolean;
  /** Row IDs explicitly deselected while select-all is active. */
  deselectedRowIds?: string[];
  /** Server-side total record count (used to compute {count} in select-all mode). */
  totalCount?: number;
  /** Current table query state (Filter, Sorting, pagination params). */
  tableQuery?: Record<string, unknown>;
  /**
   * Consumer-supplied environment / config tokens, available as `{env.KEY}` in any
   * schema string. Use this to pass runtime values that are not part of the row
   * (e.g., external app URLs, tenant-specific prefixes).
   *
   * Example — pass the participant portal URL:
   * ```tsx
   * <DynamicTableV2 envTokens={{ participantViewUrl: process.env.REACT_APP_PARTICIPANT_VIEW_URL }} />
   * ```
   * Schema usage:
   * ```json
   * "responseUrl": "{env.participantViewUrl}/?impersonateToken={response.token}"
   * ```
   */
  envTokens?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function computeCount(ctx: TemplateContext): number {
  if (ctx.selectAllActive) {
    return (ctx.totalCount ?? ctx.selectedKeys?.length ?? 0) -
      (ctx.deselectedRowIds?.length ?? 0);
  }
  return ctx.selectedKeys?.length ?? 0;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve all `{token}` placeholders in a string template against the given context.
 * Returns the resolved string. Unknown tokens are left as-is.
 *
 * Supported in: endpoint, path, url, fileName, confirm title/message,
 * successMessage, errorMessage, state values, and any string payload value.
 */
export function resolveTemplate(template: string, ctx: TemplateContext): string {
  let result = template;

  // {env.KEY} → consumer-supplied env/config token
  if (ctx.envTokens) {
    result = result.replace(
      /\{env\.(\w+)\}/g,
      (_, key) => String(ctx.envTokens![key] ?? ''),
    );
  }

  // {rowId} → resolved row primary key
  result = result.replace(
    /\{rowId\}/g,
    ctx.row ? String(ctx.row._resolvedRowId ?? '') : '',
  );

  // {count} → selected count (respects select-all-pages subtraction)
  result = result.replace(/\{count\}/g, String(computeCount(ctx)));

  // {selectedCount} → raw checked-rows length (current page only)
  result = result.replace(
    /\{selectedCount\}/g,
    String(ctx.selectedKeys?.length ?? 0),
  );

  // {totalCount} → server-side total
  result = result.replace(/\{totalCount\}/g, String(ctx.totalCount ?? 0));

  // {row.fieldName} → coerced field value
  result = result.replace(
    /\{row\.(\w+)\}/g,
    (_, field) => (ctx.row ? String(ctx.row[field] ?? '') : ''),
  );

  return result;
}

/**
 * Resolve a single JSON value (string, array, object, or primitive) against
 * the given context. Handles typed sentinel values, recursive objects/arrays,
 * and string interpolation uniformly.
 *
 * Typed sentinels (entire-value match) preserve the original type in the output
 * so that payload arrays remain `string[]`, booleans remain `boolean`, etc.
 */
export function resolveValue(value: unknown, ctx: TemplateContext): unknown {
  if (typeof value === 'string') {
    // --- Typed sentinels (must be the whole value) ---
    if (value === '{selectedRowIds}')  return ctx.selectedKeys ?? [];
    if (value === '{deselectedRowIds}') return ctx.deselectedRowIds ?? [];
    if (value === '{selectAllActive}') return ctx.selectAllActive ?? false;
    if (value === '{tableQuery}')      return ctx.tableQuery ?? {};

    // --- String interpolation ---
    return resolveTemplate(value, ctx);
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveValue(item, ctx));
  }

  if (typeof value === 'object' && value !== null) {
    const resolved: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      // Special spread: { "...": "{tableQuery}" } merges tableQuery into parent
      if (key === '...' && val === '{tableQuery}') {
        Object.assign(resolved, ctx.tableQuery ?? {});
      } else {
        resolved[key] = resolveValue(val, ctx);
      }
    }
    return resolved;
  }

  // number, boolean, null — pass through unchanged
  return value;
}
